import React, { useMemo, useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Alert,
  Tooltip,
  IconButton,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Chip,
  TablePagination,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import LoginIcon from "@mui/icons-material/Login";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WindowIcon from "@mui/icons-material/Window";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SearchIcon from "@mui/icons-material/Search";
import color from "../../Constants/colors";
import Loading from "../Common/Loading";
import { useAuthLogs } from "../../Query/useMonitoring";
import { useQueryClient } from "@tanstack/react-query";
import { getLevelColor, getMessageStyle } from "../../Helpers/stringHelpers";
import { parseLogs } from "../../Helpers/logHelpers";
import { useTranslation } from "react-i18next";

const LevelBadge: React.FC<{ level: string }> = ({ level }) => {
  const colors = getLevelColor(level);
  return (
    <Chip
      label={level}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.65rem",
        letterSpacing: "0.06em",
        backgroundColor: color.primary,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        borderRadius: "6px",
        height: 24,
      }}
    />
  );
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactElement;
  accentColor: string;
}> = ({ title, value, icon, accentColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.5, sm: 2.5 },
      borderRadius: "14px",
      flex: { xs: "1 1 calc(50% - 8px)", sm: "1 1 200px" },
      maxWidth: { xs: "calc(50% - 8px)", sm: 260 },
      minWidth: { xs: "calc(50% - 8px)", sm: 180 },
      backgroundColor: color.primary,
      border: `1px solid ${color.gray200}`,
      position: "relative",
      overflow: "hidden",
      transition: "box-shadow 0.2s, transform 0.2s",
      "&:hover": {
        boxShadow: "0 4px 20px rgba(5,77,127,0.10)",
        transform: "translateY(-2px)",
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        backgroundColor: accentColor,
        borderRadius: "14px 14px 0 0",
      }}
    />
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: `${accentColor}14`,
          color: accentColor,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: { xs: "0.6rem", sm: "0.7rem" },
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: color.gray500,
        }}
      >
        {title}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 800,
        fontSize: "2rem",
        lineHeight: 1.1,
        color: color.textPrimary,
      }}
    >
      {value}
    </Typography>
  </Paper>
);

const HighlightedMessage: React.FC<{ message: string }> = ({ message }) => {
  return (
    <Typography
      sx={{
        fontSize: { xs: "0.6rem", sm: "0.8125rem" },
        fontFamily: "'Roboto Mono', monospace",
        ...getMessageStyle(message),
      }}
    >
      {message}
    </Typography>
  );
};

const AuthMonitoring: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt } = useAuthLogs();
  const queryClient = useQueryClient();
  const { t } = useTranslation("monitoring");
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const entries = useMemo(() => {
    if (!data?.logs) return [];
    return parseLogs(data.logs).reverse(); // newest first
  }, [data]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      const matchesSearch =
        !searchTerm ||
        entry.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.timestamp.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLevel =
        levelFilter === "ALL" ||
        entry.level.toUpperCase() === levelFilter.toUpperCase();
      return matchesSearch && matchesLevel;
    });
  }, [entries, searchTerm, levelFilter]);

  const paginatedEntries = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredEntries.slice(start, start + rowsPerPage);
  }, [filteredEntries, page, rowsPerPage]);

  const stats = useMemo(() => {
    const total = entries.length;
    const successes = entries.filter((e) => e.message.includes("✅")).length;
    const failures = entries.filter((e) => e.message.includes("❌")).length;
    const sso = entries.filter((e) => e.message.includes("🪟")).length;
    return { total, successes, failures, sso };
  }, [entries]);

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={color.accent}
        color2={color.secondary}
      />
    );
  }

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">
          {t("authFailedToLoadLogs", {
            message: (error as Error)?.message || t("unknownError"),
          })}
        </Alert>
      </Container>
    );
  }

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : "";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: color.primary,
        py: 4,
        px: { xs: 1, sm: 2 },
        overflowX: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: { xs: "1.5rem", sm: "1.75rem" },
              color: color.secondary,
              mb: 0.5,
              wordBreak: "break-word",
            }}
          >
            {t("authMonitoring")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                color: color.gray500,
              }}
            >
              {t("authAutoRefreshWithLastUpdated", { time: lastUpdated })}
            </Typography>
            <Tooltip title={t("authRefreshNow")}>
              <IconButton
                size="small"
                onClick={() =>
                  queryClient.invalidateQueries({ queryKey: ["authLogs"] })
                }
                sx={{
                  color: color.secondary,
                  "&:hover": { color: color.accent },
                }}
              >
                <RefreshIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
            mb: 4,
          }}
        >
          <StatCard
            title={t("authTotalEvents")}
            value={stats.total.toLocaleString()}
            icon={<EventNoteIcon />}
            accentColor={color.secondary}
          />
          <StatCard
            title={t("authSuccessful")}
            value={stats.successes.toLocaleString()}
            icon={<LoginIcon />}
            accentColor={color.green700}
          />
          <StatCard
            title={t("authFailures")}
            value={stats.failures.toLocaleString()}
            icon={<ErrorOutlineIcon />}
            accentColor={color.red600}
          />
          <StatCard
            title={t("authSSOEvents")}
            value={stats.sso.toLocaleString()}
            icon={<WindowIcon />}
            accentColor={color.blue700}
          />
        </Box>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "14px",
            backgroundColor: color.primary,
            border: `1px solid ${color.gray200}`,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            overflowX: "auto",
          }}
        >
          <TextField
            size="small"
            placeholder={t("authSearchLogs")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: color.gray500, fontSize: "1.1rem" }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 300px" },
              minWidth: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.secondary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.secondary,
                },
                "& input": {
                  color: color.textPrimary,
                },
                "& input::placeholder": {
                  color: color.gray500,
                  opacity: 1,
                },
              },
            }}
          />
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 140 },
              flex: { xs: "1 1 100%", sm: "0 0 140px" },
            }}
          >
            <Select
              value={levelFilter}
              onChange={(e) => {
                setLevelFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                borderRadius: "10px",
                fontSize: { xs: "0.65rem", sm: "0.85rem" },
                color: color.textPrimary,
                "& .MuiSelect-icon": { color: color.gray500 },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.secondary,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.accent,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: color.secondary,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: color.white,
                    color: color.textPrimary,
                    border: `1px solid ${color.gray200}`,
                    borderRadius: "10px",
                    "& .MuiMenuItem-root": {
                      fontSize: { xs: "0.7rem", sm: "0.85rem" },
                      bgcolor: "white",
                      color: color.textPrimary,
                      "&:hover": { bgcolor: color.gray50 },
                      "&.Mui-selected": {
                        bgcolor: color.gray50,
                        "&:hover": { bgcolor: color.gray50 },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="ALL">{t("authAllLevels")}</MenuItem>
              <MenuItem value="INFO">{t("authInfoLevel")}</MenuItem>
              <MenuItem value="WARNING">{t("authWarningLevel")}</MenuItem>
              <MenuItem value="ERROR">{t("authErrorLevel")}</MenuItem>
            </Select>
          </FormControl>
          <Typography
            sx={{
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              color: color.gray500,
              ml: "auto",
              whiteSpace: "nowrap",
            }}
          >
            {filteredEntries.length.toLocaleString()} {t("of")}{" "}
            {entries.length.toLocaleString()} {t("entries")}
          </Typography>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            backgroundColor: color.primary,
            border: `1px solid ${color.gray200}`,
            overflowX: "auto",
          }}
        >
          <TableContainer
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: color.secondary }}>
                  <TableCell
                    sx={{
                      color: color.white,
                      fontWeight: 700,
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      border: "none",
                      py: 1.5,
                      pl: 2,
                      width: { xs: "25%", sm: 200 },
                    }}
                  >
                    {t("authTableHeaderTimestamp")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: color.white,
                      fontWeight: 700,
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      border: "none",
                      py: 1.5,
                      width: { xs: "25%", sm: 100 },
                    }}
                  >
                    {t("authTableHeaderLevel")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.white,
                      fontWeight: 700,
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      border: "none",
                      py: 1.5,
                      pr: 2,
                      width: { xs: "50%", sm: "auto" },
                    }}
                  >
                    {t("authTableHeaderMessage")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedEntries.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{ py: 6, border: "none" }}
                    >
                      <SecurityIcon
                        sx={{ fontSize: "3rem", color: color.gray200, mb: 1 }}
                      />
                      <Typography
                        sx={{ color: color.gray500, fontSize: "0.9rem" }}
                      >
                        {t("authNoLogEntries")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedEntries.map((entry, idx) => (
                    <TableRow
                      key={`${entry.timestamp}-${idx}`}
                      sx={{
                        backgroundColor:
                          idx % 2 === 0 ? color.primary : color.gray50,
                        "&:hover": { backgroundColor: color.blue50 },
                        transition: "background-color 0.15s",
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: "'Roboto Mono', monospace",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          fontWeight: 500,
                          color: color.textSecondary,
                          border: "none",
                          py: 1.2,
                          pl: 3,
                          whiteSpace: { xs: "normal", sm: "nowrap" },
                        }}
                      >
                        {entry.timestamp}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ border: "none", py: 1.2 }}
                      >
                        <LevelBadge level={entry.level} />
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "none",
                          py: 1.2,
                          pr: 3,
                          wordBreak: "break-word",
                        }}
                      >
                        <HighlightedMessage message={entry.message} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredEntries.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 250]}
            sx={{
              borderTop: `1px solid ${color.gray200}`,
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: "0.8rem",
                  color: color.gray500,
                },
            }}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default AuthMonitoring;
